import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { arrayRemove, arrayUnion, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { COLORES } from '../../utils/etiquetasDeChat';

/**
 * PONER Y QUITAR ETIQUETAS A UN CHAT (punto 11): cualquier miembro. Se escribe en el chat con `arrayUnion` /
 * `arrayRemove`, así que dos personas marcando a la vez no se pisan. Las reglas exigen una lista de hasta 30.
 * Quien gestiona (owner y admin) tiene además el acceso al gestor del catálogo.
 *
 * La lista se abre DENTRO DEL FLUJO, ocupando su propia fila de la barra (que envuelve), y no flotando: en un teléfono
 * el botón puede caer al principio de una fila y un panel anclado a su derecha saldría de la pantalla, recortado por el
 * `overflow: hidden` del contenedor del chat (revisión independiente, 🟠2).
 */
export default function SelectorDeEtiquetas({ orgId, chatId, chat, catalogo, puedeGestionar, onGestionar, botonClassName }) {
  const { t } = useTranslation('chats');
  const [abierto, setAbierto] = useState(false);
  const [error, setError] = useState(null);
  const caja = useRef(null);
  const puestas = Array.isArray(chat?.etiquetas) ? chat.etiquetas : [];

  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('touchstart', fuera, { passive: true });
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('touchstart', fuera); };
  }, [abierto]);

  const alternar = async (id) => {
    setError(null);
    try {
      await updateDoc(doc(db, 'organizations', orgId, 'chats', chatId), {
        etiquetas: puestas.includes(id) ? arrayRemove(id) : arrayUnion(id),
      });
    } catch (e) {
      console.error('[etiquetas] no se pudo cambiar la etiqueta del chat:', e);
      setError(t('labels.errors.generico'));
    }
  };

  return (
    <div ref={caja} style={{ flex: abierto ? '1 1 100%' : '0 0 auto', minWidth: 0 }}>
      <button type="button" className={botonClassName} onClick={() => setAbierto((v) => !v)} aria-expanded={abierto}>
        🏷️ {t('labels.button')}
      </button>
      {abierto && (
        <div style={{
          marginTop: '0.4rem', background: '#fff', border: '1px solid #e2e8f0',
          borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', padding: '0.5rem', width: 'min(20rem, 100%)',
          boxSizing: 'border-box', maxHeight: '50vh', overflowY: 'auto',
        }}>
          {catalogo.map((e) => (
            <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0.25rem', cursor: 'pointer', fontSize: 14 }}>
              <input type="checkbox" checked={puestas.includes(e.id)} onChange={() => alternar(e.id)} />
              <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: COLORES[e.color] || COLORES.gris, flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.nombre}</span>
            </label>
          ))}
          {!catalogo.length && (
            <p style={{ margin: '0.25rem', color: '#718096', fontSize: 13 }}>
              {puedeGestionar ? t('labels.emptyManager') : t('labels.emptyMember')}
            </p>
          )}
          {error && <div role="alert" style={{ color: '#c53030', fontSize: 13, padding: '0.25rem' }}>{error}</div>}
          {puedeGestionar && (
            <button type="button" onClick={() => { setAbierto(false); onGestionar(); }} style={{ marginTop: '0.4rem', width: '100%' }}>
              {t('labels.manage')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
